import {
    AllFilter,
    AmountType,
    AndFilter,
    BookingEngineSource,
    EqualsFilter,
    FeeScope,
    FeeScopeModifier,
    InFilter,
    InProductScheduleFilter,
    LineItemCode,
    LineItemDestination,
    LineItemTemplate,
    LineItemTemplateObject,
    LineItemType,
    OrFilter,
    PurchaseRule,
    PurchaseRuleAction,
    PurchaseRuleActionObject,
    PurchaseRuleFilterObject,
    PurchaseRuleLink,
    PurchaseRuleLinkEntityObject,
    PurchaseRuleModifierOperation,
    PurchaseRuleShortcut,
    PurchaseRuleShortcutTemplateCode,
    PurchaseRuleShortcutType,
    RegexMatchFilter,
    RuleFilterItem,
    Tag,
} from "types";
import { api } from "../../../api/api";
import { getEqualFilter } from "../../../models/PurchaseRule";
import { getSequence } from "../../../models/PurchaseRuleLink";
import { createOrFilter } from "../../../models/RuleFilter";
import { TaxAndFeeFormValues } from "./TaxAndFeeFormValues";

export const isMerchandiseOnlyTerms = (values: TaxAndFeeFormValues): boolean => {
    const isAbsoluteMerchandise = values.amountType === AmountType.ABSOLUTE && values.scope === FeeScope.MERCHANDISE;
    if (isAbsoluteMerchandise) {
        return true;
    }

    return (
        values.amountType === AmountType.PERCENT &&
        values.scopeModifierPerOuting?.length === 1 &&
        values.scopeModifierPerOuting?.includes(FeeScopeModifier.WARE_ONLY)
    );
};

/**
 * Check if a PurchaseRule is merchandise-only based on its shortcut configuration
 */
export const isMerchandiseOnlyPurchaseRule = (purchaseRule: PurchaseRule): boolean => {
    const shortcut = purchaseRule.actions[0]?.shortcut;
    if (!shortcut) {
        return false;
    }

    const templateCodes = shortcut.options?.templateCodes ?? [];

    if (
        shortcut.type === PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT ||
        shortcut.type === PurchaseRuleShortcutType.PERCENT
    ) {
        return templateCodes.length === 1 && templateCodes[0] === PurchaseRuleShortcutTemplateCode.WARE_SUBTOTAL;
    }

    return false;
};

export const isPurchaseRuleAppliedToMerchandise = (
    purchaseRuleLink: PurchaseRuleLink | undefined,
    merchandiseId: ID,
): boolean => {
    if (!purchaseRuleLink?.entities?.length) {
        return false;
    }

    const hasSellerEntity = purchaseRuleLink.entities.some(
        (entity) => entity.object === PurchaseRuleLinkEntityObject.SELLER,
    );

    if (hasSellerEntity) {
        return true;
    }

    return purchaseRuleLink.entities.some(
        (entity) => entity.object === PurchaseRuleLinkEntityObject.WARE && entity.id === merchandiseId,
    );
};

/**
 * Check if merchandise selection is structurally disabled for a PurchaseRule.
 * Merchandise is enabled only for:
 * - $ Per Reservation (ABSOLUTE_PER_PURCHASE)
 * - $ Per Merchandise (ABSOLUTE_PER_UNIT with WARE_SUBTOTAL)
 * - % of entire purchase amount (PERCENT with PRE_TAX_SUBTOTAL/PRE_TAX_FEE_SUBTOTAL or no codes)
 * - % of merchandise amount (PERCENT with WARE_SUBTOTAL)
 */
export const isMerchandiseDisabledForPurchaseRule = (purchaseRule: PurchaseRule): boolean => {
    const shortcut = purchaseRule.actions[0]?.shortcut;
    if (!shortcut) {
        return true;
    }

    const templateCodes = (shortcut.options?.templateCodes ?? []) as PurchaseRuleShortcutTemplateCode[];

    if (shortcut.type === PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE) {
        return false;
    }

    if (shortcut.type === PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT) {
        return !(templateCodes.length === 1 && templateCodes[0] === PurchaseRuleShortcutTemplateCode.WARE_SUBTOTAL);
    }

    if (shortcut.type === PurchaseRuleShortcutType.PERCENT) {
        const merchandiseEnabledCodes = new Set<PurchaseRuleShortcutTemplateCode>([
            PurchaseRuleShortcutTemplateCode.WARE_SUBTOTAL,
            PurchaseRuleShortcutTemplateCode.PRE_TAX_SUBTOTAL,
            PurchaseRuleShortcutTemplateCode.PRE_TAX_FEE_SUBTOTAL,
        ]);

        // No codes = entire purchase amount → enabled
        if (templateCodes.length === 0) {
            return false;
        }

        return !templateCodes.some((code) => merchandiseEnabledCodes.has(code));
    }

    return true;
};

export const getIsMerchandiseSelectionDisabled = (values: TaxAndFeeFormValues): boolean => {
    const isAbsoluteAddonFee = values.amountType === AmountType.ABSOLUTE && values.scope === FeeScope.ADDON;
    if (isAbsoluteAddonFee) {
        return true;
    }

    const isPercentagePickupOnly =
        values.amountType === AmountType.PERCENT &&
        !values.scopeModifierPerOuting?.includes(FeeScopeModifier.WARE_ONLY) &&
        values.scopeModifierPerOuting?.includes(FeeScopeModifier.PICKUP_ONLY);
    if (isPercentagePickupOnly) {
        return true;
    }

    const isPercentageWithAddonModifiers =
        values.amountType === AmountType.PERCENT &&
        !values.scopeModifierPerOuting?.includes(FeeScopeModifier.WARE_ONLY) &&
        (values.scopeModifierPerOuting?.includes(FeeScopeModifier.ADDON_ONLY) ||
            values.scopeModifierPerOuting?.includes(FeeScopeModifier.EXCLUDE_ADDON));
    if (isPercentageWithAddonModifiers) {
        return true;
    }

    const isAbsolutePerPersonPerDay =
        values.amountType === AmountType.ABSOLUTE &&
        values.scope === FeeScope.PERSON &&
        values.scopeModifierPerPerson === FeeScopeModifier.PER_DAY;
    if (isAbsolutePerPersonPerDay) {
        return true;
    }
    const isPerPersonWithoutModifier =
        values.scope === FeeScope.PERSON && !values.scopeModifierPerPerson && values.amountType === AmountType.ABSOLUTE;
    if (isPerPersonWithoutModifier) {
        return true;
    }

    return false;
};

export const getIsExperienceSelectionDisabled = (values: TaxAndFeeFormValues): boolean => {
    return isMerchandiseOnlyTerms(values);
};

interface LineItemTemplatePayload {
    object: LineItemTemplateObject;
    type: LineItemType;
    name: string;
    destination: LineItemDestination;
    visible: boolean;
    includeInAmount: boolean;
    excludeFromUnitPrice?: boolean;
    seller: { id: ID };
}

export const saveLineItemTemplate = async (sellerId: ID, values: TaxAndFeeFormValues, templateId?: ID) => {
    const lineItemTemplatePayload: LineItemTemplatePayload = {
        destination: LineItemDestination.SELLER,
        excludeFromUnitPrice: values.scope === FeeScope.OUTING && values.amountType === AmountType.ABSOLUTE,
        includeInAmount: true,
        name: values.title,
        object: LineItemTemplateObject.DEFAULT,
        seller: { id: sellerId },
        type: values.type,
        visible: true,
    };

    return templateId
        ? api.put<LineItemTemplate>(`/lineItemTemplates/${templateId}`, lineItemTemplatePayload)
        : api.post<LineItemTemplate>("/lineItemTemplates", lineItemTemplatePayload);
};

export interface PurchaseRulePayload {
    name: string;
    filter?: AndFilter | null;
    actions: PurchaseRuleAction[];
    seller: { id: ID };
    tags?: Tag[];
}

export const savePurchaseRule = async (
    values: TaxAndFeeFormValues,
    template: LineItemTemplate,
    purchaseRule: PurchaseRule | null,
    subTotalLineItemTemplates: LineItemTemplate[],
) => {
    const actions: PurchaseRuleAction[] =
        purchaseRule?.actions.map((action) => ({ id: action.id, object: action.object, _remove: true })) ?? [];

    const updatedAction: PurchaseRuleAction = {
        id: purchaseRule ? purchaseRule.actions[0].id : undefined,
        object: PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION,
        shortcut: getShortcut(values, subTotalLineItemTemplates),
        template,
    };
    const index = purchaseRule && updatedAction.id ? actions.findIndex((action) => action.id === updatedAction.id) : -1;
    if (index !== -1) {
        actions[index] = updatedAction;
    } else {
        actions.push(updatedAction);
    }

    const purchaseRulePayload: PurchaseRulePayload = {
        actions,
        filter: getFilters(values),
        tags: [{ id: values.type }],
        name: template.name,
        seller: template.seller,
    };

    return purchaseRule?.id
        ? api.put<PurchaseRule>(`/purchaseRules/${purchaseRule.id}`, purchaseRulePayload)
        : api.post<PurchaseRule>("/purchaseRules", purchaseRulePayload);
};

export const getShortcut = (
    values: TaxAndFeeFormValues,
    subTotalLineItemTemplates: LineItemTemplate[],
): PurchaseRuleShortcut => {
    const shortcut: PurchaseRuleShortcut = {
        field: "amount",
        operation: PurchaseRuleModifierOperation.SET,
        amount: values.amount ?? 0,
        type: PurchaseRuleShortcutType.PERCENT,
        options: {},
    };

    const templateIds: string[] = [];

    // Set shortcut type and options based on the selected choice
    if (values.amountType !== AmountType.ABSOLUTE) {
        shortcut.type = PurchaseRuleShortcutType.PERCENT;
        shortcut.amount = values.percent ?? 0;

        const selectedModifiers = values.scopeModifierPerOuting || [];

        const allSelected = [
            FeeScopeModifier.EXCLUDE_ADDON,
            FeeScopeModifier.EQUIPMENT_ONLY,
            FeeScopeModifier.ADDON_ONLY,
            FeeScopeModifier.PICKUP_ONLY,
            FeeScopeModifier.WARE_ONLY,
        ].every((option) => selectedModifiers.includes(option));

        if (allSelected) {
            shortcut.options =
                values.type === LineItemType.TAX
                    ? { templateCodes: [PurchaseRuleShortcutTemplateCode.PRE_TAX_SUBTOTAL] }
                    : { templateCodes: [PurchaseRuleShortcutTemplateCode.PRE_TAX_FEE_SUBTOTAL] };
        } else {
            const templateCodes: string[] = [];

            if (selectedModifiers.includes(FeeScopeModifier.EXCLUDE_ADDON)) {
                // If exclude add-on is selected, and  in case the selected demographics is not all,
                // pass the selected demographics ids in templateIds, else pass the demographic subtotal template code
                const selectedDemographics = values.selectedDemographics?.selected ?? [];
                if (selectedDemographics.length > 0) {
                    templateIds.push(...selectedDemographics);
                } else {
                    templateCodes.push(
                        PurchaseRuleShortcutTemplateCode.DEMOGRAPHIC,
                        PurchaseRuleShortcutTemplateCode.DISCOUNT_SUBTOTAL,
                    );
                }
            }

            if (selectedModifiers.includes(FeeScopeModifier.EQUIPMENT_ONLY)) {
                const selectedEquipment = values.selectedEquipment?.selected ?? [];
                if (selectedEquipment.length > 0) {
                    templateIds.push(...selectedEquipment);
                } else {
                    templateCodes.push(
                        PurchaseRuleShortcutTemplateCode.EQUIPMENT,
                        PurchaseRuleShortcutTemplateCode.DISCOUNT_SUBTOTAL,
                    );
                }
            }

            if (selectedModifiers.includes(FeeScopeModifier.ADDON_ONLY)) {
                const selectedAddons = values.selectedAddOns?.selected ?? [];
                if (selectedAddons.length > 0) {
                    templateIds.push(...selectedAddons);
                } else {
                    templateCodes.push(PurchaseRuleShortcutTemplateCode.ADDON);
                }
            }

            if (selectedModifiers.includes(FeeScopeModifier.PICKUP_ONLY)) {
                templateCodes.push(PurchaseRuleShortcutTemplateCode.PICKUP_SUBTOTAL);
            }

            if (selectedModifiers.includes(FeeScopeModifier.WARE_ONLY)) {
                templateCodes.push(PurchaseRuleShortcutTemplateCode.WARE_SUBTOTAL);
            }

            if (templateCodes.length === 0 && templateIds.length === 0) {
                shortcut.options =
                    values.type === LineItemType.TAX
                        ? { templateCodes: [PurchaseRuleShortcutTemplateCode.PRE_TAX_SUBTOTAL] }
                        : { templateCodes: [PurchaseRuleShortcutTemplateCode.PRE_TAX_FEE_SUBTOTAL] };
            } else if (templateIds.length > 0) {
                const codesToCheck = [
                    PurchaseRuleShortcutTemplateCode.DEMOGRAPHIC,
                    PurchaseRuleShortcutTemplateCode.DISCOUNT_SUBTOTAL,
                    PurchaseRuleShortcutTemplateCode.EQUIPMENT,
                    PurchaseRuleShortcutTemplateCode.ADDON,
                    PurchaseRuleShortcutTemplateCode.PICKUP_SUBTOTAL,
                    PurchaseRuleShortcutTemplateCode.WARE_SUBTOTAL,
                ];
                const templateCodeMap: Partial<Record<LineItemCode, ID>> = {};
                for (const template of subTotalLineItemTemplates) {
                    templateCodeMap[template.code] = template.id;
                }
                for (const code of codesToCheck) {
                    if (templateCodes.includes(code)) {
                        const templateId = templateCodeMap[code as unknown as LineItemCode] ?? null;
                        if (templateId) {
                            templateIds.push(templateId);
                        }
                    }
                }

                shortcut.options = { templateIds };
            } else {
                shortcut.options = { templateCodes };
            }
        }
    }

    if (values.amountType === AmountType.ABSOLUTE) {
        // Absolute payment chosen, so fixed amount
        switch (values.scope) {
            case FeeScope.PERSON:
                // Per purchasable demographic — applicable on demographic subtotal
                // Or pass demographic ids in templateIds if some demographics selected
                shortcut.options = getShortcutTemplateOptions(values);
                shortcut.type = values.scopeModifierPerPerson
                    ? PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT_PER_DAY // Per person per day
                    : PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT; // Per person per trip
                break;

            case FeeScope.EQUIPMENT:
                // Per purchasable equipment — applicable on equipment subtotal
                // Or pass equipment template ids if some equipment selected
                shortcut.type = values.scopeModifierPerPerson
                    ? PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT_PER_DAY
                    : PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT;
                shortcut.options = values.selectedEquipment?.all
                    ? { templateCodes: [PurchaseRuleShortcutTemplateCode.EQUIPMENT] }
                    : { templateIds: values.selectedEquipment?.selected ?? [] };
                break;

            case FeeScope.ADDON:
                // Per add-on, so applicable on addon subtotal
                shortcut.type = PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT;
                // Pass addons ids in templateIds if some Addons selected
                shortcut.options = values.selectedAddOns.all
                    ? {
                          templateCodes: [
                              PurchaseRuleShortcutTemplateCode.ADDON,
                              PurchaseRuleShortcutTemplateCode.PICKUP_SUBTOTAL,
                          ],
                      }
                    : { templateIds: values.selectedAddOns.selected };
                break;

            case FeeScope.MERCHANDISE:
                // Per merchandise, so applicable on merchandise subtotal
                shortcut.type = PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT;
                shortcut.options = { templateCodes: [PurchaseRuleShortcutTemplateCode.WARE_SUBTOTAL] };
                break;

            case FeeScope.OUTING:
                // Per outing, so applicable on the entire purchase (no option needs to be specified)
                shortcut.type = PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE;
                break;
            default:
                break;
        }
    }

    return shortcut;
};

export const getFilters = (values: TaxAndFeeFormValues): AndFilter | null => {
    const filters = new Array<InFilter | RegexMatchFilter | OrFilter>();
    const sourceFilters = new Array<InFilter | RegexMatchFilter | OrFilter>();
    const channelFilters = new Array<InFilter>();
    const mongodbObjectIdRegexPattern = "/^[a-f0-9]{24}/i";

    // When all experiences are selected and few memberships are selected, or vice versa
    // Add experience and memberships ID filter
    const experienceAndMembershipFilter = getProductScopeFilter(values);
    if (experienceAndMembershipFilter?.filters.length !== 0) {
        filters.push(experienceAndMembershipFilter);
    }

    // Check and update purchase sources
    const appAndIntegrationSources = [BookingEngineSource.EXPEDIA, BookingEngineSource.VIATOR];
    const hasAnyAppAndIntegrationSources = values.purchaseSources.some((source: any) =>
        appAndIntegrationSources.includes(source),
    );

    // Add Expedia/viator sources to purchase sources if app and integration is enabled
    if (values.isAppAndIntegrationEnabled && !hasAnyAppAndIntegrationSources) {
        values.purchaseSources.push(...appAndIntegrationSources);
    } else if (!values.isAppAndIntegrationEnabled && hasAnyAppAndIntegrationSources) {
        // Remove Expedia/viator sources from purchase sources if app and integration is disabled
        values.purchaseSources = values.purchaseSources.filter(
            (source: any) => !appAndIntegrationSources.includes(source),
        );
    }

    // Add source filters
    if (values.isAppAndIntegrationEnabled) {
        sourceFilters.push(getRegexMatchFilter("source", mongodbObjectIdRegexPattern));
    }

    if (values.purchaseSources.length > 0) {
        sourceFilters.push(getInFilter("source", values.purchaseSources));
    }

    // Add selected partners filter
    if (!values.selectedPartners.all) {
        channelFilters.push(getInFilter("affiliate.id", [...values.selectedPartners.selected, null]));
    }

    if (!values.selectedOperators.all) {
        channelFilters.push(getInFilter("partnerSeller.id", [...values.selectedOperators.selected, null]));
    }

    // Combine source filters if any
    if (filters.length > 0 || sourceFilters.length > 0) {
        if (sourceFilters.length > 1) {
            filters.push(getOrFilter(sourceFilters));
        } else if (sourceFilters.length === 1) {
            filters.push(sourceFilters[0]);
        }

        // This tax/fee should apply when either PartnerSeller or affiliate is there in selected list
        // or shouldn't exist (null), so we should add these filters directly.
        filters.push(...channelFilters);

        return getAndFilter(filters);
    }

    return null;
};

const getInFilter = (field: string, operand: Array<string | null>): InFilter => {
    return {
        object: PurchaseRuleFilterObject.IN_FILTER,
        field,
        operand,
        operator: "in",
    };
};

const getRegexMatchFilter = (field: string, operand: string): RegexMatchFilter => {
    return {
        object: PurchaseRuleFilterObject.REGEX_MATCH_FILTER,
        field,
        operand,
        operator: "in",
    };
};

const getAndFilter = (filters: Array<InFilter | RegexMatchFilter | OrFilter>): AndFilter => {
    return {
        object: PurchaseRuleFilterObject.AND_FILTER,
        filters,
        operator: "and",
    };
};

const getOrFilter = (filters: Array<InFilter | RegexMatchFilter | OrFilter | EqualsFilter>): OrFilter => {
    return {
        object: PurchaseRuleFilterObject.OR_FILTER,
        filters,
        operator: "or",
    };
};

export const getProductScopeFilter = (values: TaxAndFeeFormValues): OrFilter => {
    const filters = new Array<InFilter | EqualsFilter>();

    const experiencesState = values.selectedExperiences.all
        ? "all"
        : values.selectedExperiences.experiences.length > 0
        ? "some"
        : "none";

    const membershipsState = values.selectedMemberships.all
        ? "all"
        : values.selectedMemberships.memberships.length > 0
        ? "some"
        : "none";

    const merchandiseState = values.selectedMerchandise.all
        ? "all"
        : values.selectedMerchandise.merchandise.length > 0
        ? "some"
        : "none";

    const allTypes: string[] = [];
    const specificIds: string[] = [];

    if (experiencesState === "all") {
        allTypes.push("experience");
    } else if (experiencesState === "some") {
        specificIds.push(...values.selectedExperiences.experiences);
    }

    if (membershipsState === "all") {
        allTypes.push("membership");
    } else if (membershipsState === "some") {
        specificIds.push(...values.selectedMemberships.memberships);
    }

    if (merchandiseState === "all") {
        allTypes.push("ware");
    } else if (merchandiseState === "some") {
        specificIds.push(...values.selectedMerchandise.merchandise);
    }

    if (allTypes.length > 1) {
        filters.push(getInFilter("productScopeForTaxFees", allTypes));
    } else if (allTypes.length === 1) {
        filters.push(getEqualFilter(allTypes[0], "productScopeForTaxFees"));
    }

    if (specificIds.length > 0) {
        filters.push(getInFilter("productForTaxFees.id", specificIds));
    }

    return createOrFilter({ filters });
};

export const savePurchaseRuleLink = async (
    sellerId: ID,
    purchaseRuleId: ID,
    values: TaxAndFeeFormValues,
    purchaseRuleLinkId?: ID,
) => {
    const purchaseRuleLinksPayload: PurchaseRuleLink = {
        seller: { id: sellerId },
        entities: [],
        purchaseRules: [{ purchaseRule: { id: purchaseRuleId }, sequence: getSequence(values.type) }],
        name: "",
    };

    // If any product type has "all" selected, default to Seller level
    // Otherwise, pass specific IDs for all product types that have specific selections
    if (values.selectedExperiences.all || values.selectedMemberships.all || values.selectedMerchandise.all) {
        purchaseRuleLinksPayload.entities.push({ id: sellerId, object: PurchaseRuleLinkEntityObject.SELLER });
    } else {
        // All product types have specific selections
        const entities = [
            ...values.selectedExperiences.experiences.map((id) => ({
                id,
                object: PurchaseRuleLinkEntityObject.EXPERIENCE,
            })),
            ...values.selectedMemberships.memberships.map((id) => ({
                id,
                object: PurchaseRuleLinkEntityObject.MEMBERSHIP,
            })),
            ...values.selectedMerchandise.merchandise.map((id) => ({
                id,
                object: PurchaseRuleLinkEntityObject.WARE,
            })),
        ];

        if (entities.length === 0) {
            entities.push({ id: sellerId, object: PurchaseRuleLinkEntityObject.SELLER });
        }

        purchaseRuleLinksPayload.entities = entities;
    }

    return purchaseRuleLinkId
        ? api.put<PurchaseRuleLink>(`/purchaseRuleLinks/${purchaseRuleLinkId}`, purchaseRuleLinksPayload)
        : api.post<PurchaseRuleLink>("/purchaseRuleLinks", purchaseRuleLinksPayload);
};

/**
 * Directly add a merchandise ID to a purchase rule's product scope and its link's entities.
 * This avoids needing to reconstruct the full TaxAndFeeFormValues.
 */
export const addMerchandiseToPurchaseRule = async (
    purchaseRule: PurchaseRule,
    purchaseRuleLink: PurchaseRuleLink,
    merchandiseId: ID,
    sellerId: ID,
) => {
    const updatedFilter = addMerchandiseIdToFilter(purchaseRule.filter, merchandiseId);

    const purchaseRulePayload = {
        actions: purchaseRule.actions,
        filter: updatedFilter,
        tags: purchaseRule.tags,
        name: purchaseRule.name ?? "",
        seller: purchaseRule.seller ?? { id: sellerId },
    };

    await api.put<PurchaseRule>(`/purchaseRules/${purchaseRule.id}`, purchaseRulePayload);

    const hasSellerEntity = purchaseRuleLink.entities.some(
        (entity) => entity.object === PurchaseRuleLinkEntityObject.SELLER,
    );

    if (!hasSellerEntity) {
        const alreadyLinked = purchaseRuleLink.entities.some(
            (entity) => entity.object === PurchaseRuleLinkEntityObject.WARE && entity.id === merchandiseId,
        );

        if (!alreadyLinked) {
            const updatedLinkPayload: PurchaseRuleLink = {
                ...purchaseRuleLink,
                entities: [
                    ...purchaseRuleLink.entities,
                    { id: merchandiseId, object: PurchaseRuleLinkEntityObject.WARE },
                ],
            };

            await api.put<PurchaseRuleLink>(`/purchaseRuleLinks/${purchaseRuleLink.id}`, updatedLinkPayload);
        }
    }
};

/**
 * Traverse the purchase rule's filter tree and append a merchandise ID to the
 * `productForTaxFees.id` InFilter inside the product-scope OrFilter.
 */
const addMerchandiseIdToFilter = (
    filter: PurchaseRule["filter"],
    merchandiseId: ID,
): AndFilter | OrFilter | InFilter | InProductScheduleFilter | AllFilter | null => {
    if (!filter) {
        return filter ?? null;
    }

    if (filter.object === PurchaseRuleFilterObject.AND_FILTER) {
        // Only mutate the product-scope OrFilter (the one that holds productForTaxFees or productScopeForTaxFees)
        const filters = filter.filters.map((child) => {
            if (child.object !== PurchaseRuleFilterObject.OR_FILTER) {
                return child;
            }

            // Detect product-scope OrFilter by presence of productForTaxFees.id or productScopeForTaxFees
            const isProductScopeOrFilter = (child as OrFilter).filters.some((filter) => {
                if ((filter as any)?.field) {
                    const field = (filter as any).field as string;
                    return field === "productForTaxFees.id" || field === "productScopeForTaxFees";
                }
                return false;
            });

            return isProductScopeOrFilter ? appendMerchandiseIdToOrFilter(child, merchandiseId) : child;
        });

        return {
            ...filter,
            filters,
        };
    }

    if (filter.object === PurchaseRuleFilterObject.OR_FILTER) {
        return appendMerchandiseIdToOrFilter(filter, merchandiseId) as OrFilter;
    }

    return filter;
};

const appendMerchandiseIdToOrFilter = (filter: RuleFilterItem, merchandiseId: ID): RuleFilterItem => {
    if (filter.object !== PurchaseRuleFilterObject.OR_FILTER) {
        return filter;
    }

    const orFilter = filter as OrFilter;
    const existingInFilter = orFilter.filters.find(
        (f): f is InFilter =>
            f.object === PurchaseRuleFilterObject.IN_FILTER && (f as InFilter).field === "productForTaxFees.id",
    );

    if (existingInFilter) {
        const currentOperand = existingInFilter.operand ?? [];
        if (!currentOperand.includes(merchandiseId)) {
            return {
                ...orFilter,
                filters: orFilter.filters.map((f) =>
                    f === existingInFilter ? { ...existingInFilter, operand: [...currentOperand, merchandiseId] } : f,
                ),
            };
        }
        return orFilter;
    }

    // No productForTaxFees.id InFilter exists yet — create one
    const newInFilter: InFilter = {
        object: PurchaseRuleFilterObject.IN_FILTER,
        field: "productForTaxFees.id",
        operand: [merchandiseId],
        operator: "in",
    };

    return { ...orFilter, filters: [...orFilter.filters, newInFilter] };
};

export const getShortcutTemplateOptions = (values: TaxAndFeeFormValues) => {
    const options = values.selectedDemographics.all
        ? { templateCodes: [PurchaseRuleShortcutTemplateCode.DEMOGRAPHIC] }
        : {
              templateIds: values.selectedDemographics.selected,
              templateCodes: [] as PurchaseRuleShortcutTemplateCode[],
          };

    if (values.selectedMemberships.all || values.selectedMemberships.memberships.length > 0) {
        options.templateCodes.push(PurchaseRuleShortcutTemplateCode.MEMBERSHIP_SUBTOTAL);
    }

    if (values.selectedMerchandise.all || values.selectedMerchandise.merchandise.length > 0) {
        options.templateCodes.push(PurchaseRuleShortcutTemplateCode.WARE_SUBTOTAL);
    }

    return options;
};
